// COP Copilot — the Q1 geo-query surface (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Net-new geo/relational query AST + a PURE, zero-eval reference executor, ported from
// takbot packages/protocol/src/geo/query-ast.ts + the broker's worker/compile.ts
// (predicateFields / matchesWhere) + worker/geo.ts (the tiny geo kernel). This is the
// genuine upstream contribution: tak-tdf's EntityFilter (render-engine.ts) is kinds+ids
// ONLY; bbox / radius+near_ref / within+zone_ref / intersects is net-new here.
//
// WAVE-A SCOPE (no live stack): this is a TYPES + reference-impl PREP module. It runs the
// declarative AST against a flat row set the CALLER supplies — it is NOT wired to a live
// store. The Wave-A binding (copilotTables.ts) feeds it ONLY the host's PDP-interposed,
// already-visible envelopes (the live pdp.visibleEnvelopes() analog); a denied envelope is
// never in the row set, so a Q1 query can never count or surface it (Glomar). The live
// proof against the running PDP + two personas is Wave B (docs/COPILOT-WAVE-B-PLAN.md).
//
// THREAT MODEL (takbot browser-agent-arch.md §4/§25): DECLARATIVE JSON ONLY. Never a
// function, never an expression string. Every node maps onto a known pure op + this geo
// kernel (haversine + point-in-polygon). The executor NEVER accepts agent-authored code.
//
// COORDINATE ORDER (the live footgun): GeoJSON `[lng, lat]`; bbox `[west, south, east,
// north]`; radius center `[lng, lat]`. Rows also expose split scalar `lat`/`lng` columns
// for the cheap arithmetic path.
//
// Holds NO React, never writes the engine, imports ONLY types from the C3 SDK.

// ─── the query AST (ported faithfully from takbot @takbot/protocol query-ast.ts) ──────

export type Scalar = string | number | boolean | null;

/** A reference to a declared table view (e.g. "entities", "zones", "viewport"). */
export type TableRef = string | { table: string; as?: string };

/** Non-spatial comparison predicates. */
export type ScalarPredicate =
  | { field: string; op: "=" | "!=" | "<" | "<=" | ">" | ">="; value: Scalar }
  | { field: string; op: "in"; value: Scalar[] }
  | { field: string; op: "contains" | "starts_with" | "ends_with"; value: string }
  | { field: string; op: "is_null" | "is_not_null" };

/** Minimal GeoJSON subset (RFC 7946, coordinates as [lng,lat]). */
export interface GeoJsonPolygon {
  type: "Polygon";
  coordinates: number[][][];
}
export type GeoJsonGeometry =
  | { type: "Point"; coordinates: [number, number] }
  | { type: "LineString"; coordinates: number[][] }
  | GeoJsonPolygon;

/** Spatial predicates — the net-new geo layer. `field` defaults to "geometry". A row whose
 *  geometry is null NEVER matches a spatial predicate (tolerate, don't throw). */
export type GeoPredicate =
  | { op: "bbox"; field?: string; value: [west: number, south: number, east: number, north: number] }
  | { op: "radius"; field?: string; center?: [lng: number, lat: number]; near_ref?: string; meters: number }
  | { op: "within"; field?: string; polygon?: GeoJsonPolygon; zone_ref?: string }
  | { op: "intersects"; field?: string; geometry?: GeoJsonGeometry; zone_ref?: string };

/** Boolean combinators close the predicate algebra. */
export type Predicate =
  | ScalarPredicate
  | GeoPredicate
  | { op: "and"; predicates: Predicate[] }
  | { op: "or"; predicates: Predicate[] }
  | { op: "not"; predicate: Predicate };

export type SelectSpec = string | { field: string; as?: string };
export type OrderSpec = { field: string; dir?: "asc" | "desc"; nulls?: "first" | "last" };
export type OutputSpec = { mode: "preview" | "result_ref" | "inline"; preview_rows?: number; max_rows?: number };

/** The full query the agent emits — the `args` of a `table.query` capability call. */
export interface TableQuery {
  from: TableRef;
  where?: Predicate[];
  select?: SelectSpec[];
  order_by?: OrderSpec[];
  limit?: number;
  offset?: number;
  output?: OutputSpec;
}

/** Hard limits the host enforces before/around execution. */
export interface QueryLimits {
  max_runtime_ms: number;
  max_rows_scanned: number;
  max_rows_returned: number;
  max_result_bytes: number;
}

export const DEFAULT_QUERY_LIMITS: QueryLimits = {
  max_runtime_ms: 500,
  max_rows_scanned: 50_000,
  max_rows_returned: 500,
  max_result_bytes: 512 * 1024,
};

/** A flat projected row (one entity / zone / viewport row). */
export type Row = Record<string, unknown>;

/** A Glomar-safe error taxonomy: denied-because-invisible returns the SAME code as
 *  genuinely-unknown, so a caller cannot oracle the existence of a denied object. */
export type QueryErrorCode =
  | "unknown_table"
  | "unknown_column"
  | "unsupported_operation"
  | "limit_exceeded"
  | "schema_invalid"
  | "internal_error";

export class QueryError extends Error {
  constructor(
    readonly code: QueryErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "QueryError";
  }
}

// ─── the PURE geo kernel (ported from takbot worker/geo.ts) ───────────────────────────

const EARTH_RADIUS_M = 6_371_008.8; // IUGG mean Earth radius (metres)
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in METRES between two GeoJSON `[lng, lat]` points (haversine). */
export function haversineMeters(a: [number, number], b: [number, number]): number {
  const [lng1, lat1] = a;
  const [lng2, lat2] = b;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Is `[lng, lat]` inside the bbox `[west, south, east, north]`? Inclusive on the edges. */
export function inBbox(lng: number, lat: number, bbox: [number, number, number, number]): boolean {
  const [west, south, east, north] = bbox;
  return lng >= west && lng <= east && lat >= south && lat <= north;
}

function exteriorRing(polygon: GeoJsonPolygon): number[][] | null {
  const ring = polygon?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 3) return null;
  return ring;
}

/** Point-in-polygon by ray-casting (even–odd). Tests ONLY the exterior ring. */
export function pointInPolygon(point: [number, number], polygon: GeoJsonPolygon): boolean {
  const ring = exteriorRing(polygon);
  if (!ring) return false;
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i];
    const pj = ring[j];
    if (!pi || !pj) continue;
    const xi = pi[0] ?? 0;
    const yi = pi[1] ?? 0;
    const xj = pj[0] ?? 0;
    const yj = pj[1] ?? 0;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Axis-aligned bbox of any GeoJSON geometry, or null. */
export function geometryBbox(g: GeoJsonGeometry): [number, number, number, number] | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const eat = (lng: unknown, lat: unknown): void => {
    if (typeof lng === "number" && typeof lat === "number") {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    }
  };
  if (g.type === "Point") eat(g.coordinates[0], g.coordinates[1]);
  else if (g.type === "LineString") for (const c of g.coordinates) eat(c[0], c[1]);
  else if (g.type === "Polygon") for (const ring of g.coordinates) for (const c of ring) eat(c[0], c[1]);
  if (!Number.isFinite(west)) return null;
  return [west, south, east, north];
}

function bboxOverlap(a: [number, number, number, number], b: [number, number, number, number]): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
}

/** Pragmatic intersects: point-vs-polygon is exact point-in-poly; else bbox-overlap. */
export function geometryIntersects(a: GeoJsonGeometry, b: GeoJsonGeometry): boolean {
  if (a.type === "Point" && b.type === "Polygon") return pointInPolygon([a.coordinates[0], a.coordinates[1]], b);
  if (b.type === "Point" && a.type === "Polygon") return pointInPolygon([b.coordinates[0], b.coordinates[1]], a);
  const ba = geometryBbox(a);
  const bb = geometryBbox(b);
  if (!ba || !bb) return false;
  return bboxOverlap(ba, bb);
}

// ─── geometry coercion + row reads (ported from worker/compile.ts) ────────────────────

function asGeoJson(value: unknown): GeoJsonGeometry | null {
  if (!value || typeof value !== "object") return null;
  const g = value as { type?: unknown; coordinates?: unknown };
  if (typeof g.type !== "string" || !Array.isArray(g.coordinates)) return null;
  if (g.type === "Point" || g.type === "LineString" || g.type === "Polygon") return value as GeoJsonGeometry;
  return null;
}

/** Read a row's point as `[lng, lat]` — prefers split lat/lng cols, falls back to geometry.
 *  Null when the row carries no point (a spatial predicate never matches; tolerate null). */
function rowPoint(row: Row): [number, number] | null {
  const lng = row.lng;
  const lat = row.lat;
  if (typeof lng === "number" && typeof lat === "number") return [lng, lat];
  const g = asGeoJson(row.geometry);
  if (g && g.type === "Point") {
    const c = g.coordinates;
    if (typeof c[0] === "number" && typeof c[1] === "number") return [c[0], c[1]];
  }
  return null;
}

/** Resolve a `zone_ref` to its polygon via the (visible-only) zones map. Throws fail-closed
 *  when the ref is unknown — a hidden zone is simply unresolvable (Glomar). */
function resolveZone(zoneRef: string, zones: Record<string, GeoJsonPolygon> | undefined): GeoJsonPolygon {
  const poly = zones?.[zoneRef] ?? zones?.[zoneRef.trim().toLowerCase()];
  if (!poly) throw new QueryError("unknown_column", `unknown zone_ref "${zoneRef}"`);
  return poly;
}

// ─── scalar predicate evaluation ──────────────────────────────────────────────────────

function cmp(op: ScalarPredicate["op"], a: unknown, b: unknown): boolean {
  switch (op) {
    case "<":
      return (a as number) < (b as number);
    case "<=":
      return (a as number) <= (b as number);
    case ">":
      return (a as number) > (b as number);
    case ">=":
      return (a as number) >= (b as number);
    default:
      return false;
  }
}

function evalScalar(p: ScalarPredicate, row: Row): boolean {
  const v = row[p.field];
  switch (p.op) {
    case "=":
      return v === p.value;
    case "!=":
      return v !== p.value;
    case "<":
    case "<=":
    case ">":
    case ">=":
      if (v == null || p.value == null) return false;
      return cmp(p.op, v, p.value);
    case "in":
      return Array.isArray(p.value) && (p.value as Scalar[]).includes(v as Scalar);
    case "contains":
      return typeof v === "string" && v.includes(p.value);
    case "starts_with":
      return typeof v === "string" && v.startsWith(p.value);
    case "ends_with":
      return typeof v === "string" && v.endsWith(p.value);
    case "is_null":
      return v == null;
    case "is_not_null":
      return v != null;
    default:
      throw new QueryError("unsupported_operation", "unsupported scalar op");
  }
}

// ─── near_ref resolution (agent-friendly radius, ported from worker/compile.ts) ───────

/** Find a reference entity's [lng,lat] for a radius `near_ref` — exact id/callsign first,
 *  then name/type substring, then a shared token. Resolves over the VISIBLE row set only,
 *  so an agent can never use near_ref to triangulate a denied entity's location. */
function findEntityCenter(ref: string, rows: Row[]): [number, number] | null {
  const q = ref.trim().toLowerCase();
  if (!q) return null;
  const get = (r: Row, k: string): string => String(r[k] ?? "").toLowerCase();
  const center = (r: Row): [number, number] | null => {
    const lng = Number(r.lng);
    const lat = Number(r.lat);
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
  };
  for (const r of rows) if (get(r, "id") === q || get(r, "callsign") === q) { const c = center(r); if (c) return c; }
  for (const r of rows) {
    const name = get(r, "name");
    const type = get(r, "type");
    if (name.includes(q) || type.includes(q)) { const c = center(r); if (c) return c; }
  }
  const tokens = q.split(/[^a-z0-9]+/).filter((t) => t.length >= 3);
  for (const r of rows) {
    const hay = `${get(r, "name")} ${get(r, "type")} ${get(r, "callsign")} ${get(r, "kind")}`;
    if (tokens.some((t) => hay.includes(t))) { const c = center(r); if (c) return c; }
  }
  return null;
}

/** Resolve a radius `near_ref` to a concrete `center`, recursing through and/or/not.
 *  Returns a cloned predicate; the original AST is never mutated. */
function resolveNearRef(p: Predicate, rows: Row[]): Predicate {
  switch (p.op) {
    case "and":
      return { op: "and", predicates: p.predicates.map((q) => resolveNearRef(q, rows)) };
    case "or":
      return { op: "or", predicates: p.predicates.map((q) => resolveNearRef(q, rows)) };
    case "not":
      return { op: "not", predicate: resolveNearRef(p.predicate, rows) };
    case "radius":
      if (!p.center && p.near_ref) {
        const center = findEntityCenter(p.near_ref, rows);
        if (!center) throw new QueryError("unknown_column", `near_ref "${p.near_ref}" matched no entity`);
        return { ...p, center };
      }
      return p;
    default:
      return p;
  }
}

// ─── geo predicate evaluation ─────────────────────────────────────────────────────────

function evalGeo(p: GeoPredicate, row: Row, zones: Record<string, GeoJsonPolygon> | undefined): boolean {
  switch (p.op) {
    case "bbox": {
      const pt = rowPoint(row);
      if (!pt) return false;
      return inBbox(pt[0], pt[1], p.value);
    }
    case "radius": {
      const pt = rowPoint(row);
      if (!pt) return false;
      if (!p.center) throw new QueryError("schema_invalid", "radius needs a center [lng,lat] or a resolvable near_ref");
      return haversineMeters(pt, p.center) <= p.meters;
    }
    case "within": {
      const pt = rowPoint(row);
      if (!pt) return false;
      const poly = p.polygon ?? (p.zone_ref ? resolveZone(p.zone_ref, zones) : null);
      if (!poly) throw new QueryError("schema_invalid", "within needs polygon or zone_ref");
      return pointInPolygon(pt, poly);
    }
    case "intersects": {
      const g = asGeoJson(row.geometry);
      if (!g) return false;
      const target = p.geometry ?? (p.zone_ref ? resolveZone(p.zone_ref, zones) : null);
      if (!target) throw new QueryError("schema_invalid", "intersects needs geometry or zone_ref");
      return geometryIntersects(g, target);
    }
    default:
      throw new QueryError("unsupported_operation", "unsupported geo op");
  }
}

const isGeoOp = (op: string): boolean => op === "bbox" || op === "radius" || op === "within" || op === "intersects";

/** Recursively evaluate ONE predicate node against a row. Pure interpretation — no eval. */
function evalPredicate(p: Predicate, row: Row, zones: Record<string, GeoJsonPolygon> | undefined): boolean {
  const op = (p as { op: string }).op;
  if (op === "and") return (p as { predicates: Predicate[] }).predicates.every((c) => evalPredicate(c, row, zones));
  if (op === "or") return (p as { predicates: Predicate[] }).predicates.some((c) => evalPredicate(c, row, zones));
  if (op === "not") return !evalPredicate((p as { predicate: Predicate }).predicate, row, zones);
  if (isGeoOp(op)) return evalGeo(p as GeoPredicate, row, zones);
  return evalScalar(p as ScalarPredicate, row);
}

/** Collect every field name a predicate tree references (for unknown_column validation). */
function collectFields(p: Predicate, out: Set<string>): void {
  const op = (p as { op: string }).op;
  if (op === "and" || op === "or") {
    for (const c of (p as { predicates: Predicate[] }).predicates) collectFields(c, out);
    return;
  }
  if (op === "not") {
    collectFields((p as { predicate: Predicate }).predicate, out);
    return;
  }
  if (isGeoOp(op)) {
    out.add((p as GeoPredicate).field ?? "geometry");
    return;
  }
  out.add((p as ScalarPredicate).field);
}

/** Every field a predicate tree references (for the host's pre-flight column validation). */
export function predicateFields(where: Predicate[] | undefined): Set<string> {
  const out = new Set<string>();
  for (const p of where ?? []) collectFields(p, out);
  return out;
}

/** Does a row satisfy the (conjunctive) `where` set? Pure interpretation — no eval. */
export function matchesWhere(
  row: Row,
  where: Predicate[] | undefined,
  zones?: Record<string, GeoJsonPolygon>,
): boolean {
  if (!where || where.length === 0) return true;
  try {
    return where.every((p) => evalPredicate(p, row, zones));
  } catch {
    return false;
  }
}

// ─── the reference executor (Wave-A; no live store, caller supplies the visible rows) ──

export interface QueryResult {
  columns: string[];
  rows: Row[];
  stats: { rows_scanned: number; rows_returned: number };
}

/** Execute a TableQuery against a CALLER-SUPPLIED, already-visible row set + zones map.
 *
 *  WAVE-A CONTRACT: the `rows` MUST be the host's PDP-interposed delivery (the visible set
 *  only — see copilotTables.ts). This function does NOT apply any visibility filter of its
 *  own and carries NO static `visible:true` placeholder: a denied envelope is simply not in
 *  `rows`, so it can never be counted, ordered, or returned (Glomar by construction).
 *
 *  Pure + synchronous: filter (where) → near_ref resolution → order_by → offset/limit →
 *  select projection. The takbot Arquero join/group/metric/derive path is deferred to the
 *  live worker in Wave B; the Wave-A reference path covers the read/filter/geo surface the
 *  security demo needs. */
export function executeQuery(
  query: TableQuery,
  rows: Row[],
  zones?: Record<string, GeoJsonPolygon>,
  limits: QueryLimits = DEFAULT_QUERY_LIMITS,
): QueryResult {
  const scanned = rows.length;
  if (scanned > limits.max_rows_scanned) {
    throw new QueryError("limit_exceeded", `scan of ${scanned} rows exceeds the budget`);
  }

  // Resolve any radius near_ref against the VISIBLE rows (never a hidden entity).
  const where = (query.where ?? []).map((p) => resolveNearRef(p, rows));

  let out = rows.filter((r) => matchesWhere(r, where, zones));

  // order_by (stable, multi-key).
  for (const o of [...(query.order_by ?? [])].reverse()) {
    const dir = o.dir === "desc" ? -1 : 1;
    out = [...out].sort((a, b) => {
      const av = a[o.field];
      const bv = b[o.field];
      if (av == null && bv == null) return 0;
      if (av == null) return o.nulls === "first" ? -1 : 1;
      if (bv == null) return o.nulls === "first" ? 1 : -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }

  const offset = Math.max(0, query.offset ?? 0);
  const cap = Math.min(query.limit ?? limits.max_rows_returned, limits.max_rows_returned);
  out = out.slice(offset, offset + cap);

  // select projection (rename via {field, as}); absent ⇒ all source columns.
  let columns: string[];
  if (query.select && query.select.length > 0) {
    const projected: Row[] = out.map((r) => {
      const next: Row = {};
      for (const s of query.select!) {
        const field = typeof s === "string" ? s : s.field;
        const as = typeof s === "string" ? s : (s.as ?? s.field);
        next[as] = r[field];
      }
      return next;
    });
    out = projected;
    columns = query.select.map((s) => (typeof s === "string" ? s : (s.as ?? s.field)));
  } else {
    columns = out.length > 0 ? Object.keys(out[0]) : [];
  }

  return { columns, rows: out, stats: { rows_scanned: scanned, rows_returned: out.length } };
}
